import { useEffect, useRef } from 'react';
import { ArrowUpRight, X } from 'lucide-react';

import { useBannerAnnouncementStore } from '@/stores/banner-announcement';

import { useBannerAnnouncementQuery } from './api/use-banner-announcement-query';
import { BannerAnnouncementStatus, type BannerAnnouncementItem } from './types';

const BannerItem = ({
  announcement,
  onDismiss,
}: {
  announcement: BannerAnnouncementItem;
  onDismiss: (id: string) => void;
}) => {
  return (
    <div className="bg-brand-blue flex w-full flex-col border-b border-white/70 px-3 py-2 text-white last:border-b-0 sm:flex-row sm:items-center sm:gap-3 sm:px-4 sm:py-1.5">
      <div className="flex items-start justify-between sm:hidden">
        <div className="flex flex-col gap-0.5 text-[10px]">
          <span className="font-medium">{announcement.title}</span>
          <span className="text-white/90">{announcement.description}</span>
        </div>
        <button
          onClick={() => onDismiss(announcement.id)}
          className="shrink-0 rounded p-0.5 transition-opacity hover:opacity-80"
        >
          <X className="h-3.5 w-3.5" />
        </button>
      </div>
      {announcement.cta_url && (
        <a
          href={announcement.cta_url}
          target="_blank"
          rel="noopener noreferrer"
          className="mt-1 inline-flex items-center gap-0.5 text-[10px] font-medium underline underline-offset-2 transition-opacity hover:opacity-80 sm:hidden"
        >
          {announcement.cta_label ?? 'Learn more'}
          <ArrowUpRight className="h-3 w-3" />
        </a>
      )}

      <div className="hidden min-w-0 flex-1 items-center justify-center gap-2 text-xs sm:flex">
        <span className="max-w-[30ch] truncate font-medium" title={announcement.title}>
          {announcement.title}
        </span>
        <span className="h-1 w-1 shrink-0 rounded-full bg-white/70" />
        <span className="max-w-[100ch] truncate text-white/90" title={announcement.description}>
          {announcement.description}
        </span>
        {announcement.cta_url && (
          <a
            href={announcement.cta_url}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex shrink-0 items-center gap-1 font-medium underline underline-offset-2 transition-opacity hover:opacity-80"
          >
            {announcement.cta_label ?? 'Learn more'}
            <ArrowUpRight className="h-3 w-3" />
          </a>
        )}
      </div>
      <button
        onClick={() => onDismiss(announcement.id)}
        className="hidden shrink-0 rounded p-0.5 transition-opacity hover:opacity-80 sm:block"
      >
        <X className="h-3.5 w-3.5" />
      </button>
    </div>
  );
};

export const BannerAnnouncement = () => {
  const dismissedBannerIds = useBannerAnnouncementStore((s) => s.dismissedBannerIds);
  const dismissBanner = useBannerAnnouncementStore((s) => s.dismissBanner);
  const setBannerHeight = useBannerAnnouncementStore((s) => s.setBannerHeight);
  const { data } = useBannerAnnouncementQuery();
  const announcements = data?.list ?? [];
  const now = new Date();
  const visibleAnnouncements = announcements.filter(
    (a) =>
      a.status === BannerAnnouncementStatus.PUBLISHED &&
      new Date(a.end_date) > now &&
      !dismissedBannerIds.includes(a.id),
  );

  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (visibleAnnouncements.length === 0) {
      setBannerHeight(0);
      return;
    }

    const node = containerRef.current;
    if (!node) return;

    const observer = new ResizeObserver(([entry]) => {
      setBannerHeight(entry.contentRect.height);
    });
    observer.observe(node);

    return () => {
      observer.disconnect();
      setBannerHeight(0);
    };
  }, [setBannerHeight, visibleAnnouncements.length]);

  if (visibleAnnouncements.length === 0) return null;

  return (
    <div ref={containerRef} className="fixed inset-x-0 top-0 z-[70] flex w-full flex-col">
      {visibleAnnouncements.map((announcement) => (
        <BannerItem key={announcement.id} announcement={announcement} onDismiss={dismissBanner} />
      ))}
    </div>
  );
};
